// Image bytes stay in IndexedDB, outside Redux, under stable file IDs.
import { getCachedData, putCachedData, getAllOutboxItems } from "./offlineDb"
const LEGACY_CACHE = "encoaudit-images-v1"
const LEGACY_ORIGIN = "https://offline-image-cache.local/"
const API_BASE = process.env.REACT_APP_API_URL || "http://localhost:8005/api"
const inFlight = new Map()
const urlKey = (url) => {
  if (!url || /^(blob:|data:)/.test(url)) return null
  try { const parsed = new URL(url, window.location.origin); return `url:${parsed.origin}${parsed.pathname}` } catch { return null }
}
const keys = (fileId, url) => [...new Set([fileId && `image:${fileId}`, urlKey(url) && `image:${urlKey(url)}`].filter(Boolean))]

export async function cacheImageBlob(fileId, blob, url) {
  if (!blob || !blob.size) return false
  try {
    await Promise.all(keys(fileId, url).map((key) => putCachedData(key,
      fileId && key.startsWith("image:url:") ? { imageId: fileId } : blob)))
    return keys(fileId, url).length > 0
  } catch { return false }
}

export async function getCachedImageBlob(fileId, url) {
  for (const key of keys(fileId, url)) {
    try {
      const record = await getCachedData(key)
      const blob = record?.imageId ? await getCachedData(`image:${record.imageId}`) : record
      if (blob?.size && typeof blob !== "string") return blob
    } catch {}
  }
  if (fileId && typeof caches !== "undefined") {
    try {
      const stored = await (await caches.open(LEGACY_CACHE)).match(new Request(LEGACY_ORIGIN + encodeURIComponent(fileId)))
      if (stored) return await stored.blob()
    } catch {}
  }
  if (String(fileId).startsWith("pending_")) {
    try {
      const queued = (await getAllOutboxItems()).find((item) => item.type === "file-upload" &&
        [`pending_${item.clientId}`, item.clientId].includes(fileId))
      if (queued?.file?.size) return queued.file
    } catch {}
  }
  return null
}

export async function cacheImage(fileId, url) {
  if (!fileId && !url) return false
  const key = fileId || urlKey(url)
  if (inFlight.has(key)) return inFlight.get(key)
  const work = (async () => {
    if (await getCachedImageBlob(fileId, url)) return true
    if (navigator.onLine === false && !/^(blob:|data:)/.test(url || "")) return false
    let blob
    if (url) {
      try { const response = await fetch(url, { mode: "cors" }); if (response.ok) blob = await response.blob() } catch {}
    }
    // Authenticated API fallback avoids S3 CORS restrictions and expired links.
    if (!blob && /^[a-f\d]{24}$/i.test(fileId || "")) {
      try {
        const response = await fetch(`${API_BASE}/files/${fileId}/content`, { credentials: "include",
          headers: { Authorization: `Bearer ${localStorage.getItem("token") || ""}` } })
        if (response.ok) blob = await response.blob()
      } catch {}
    }
    if (!blob || blob.type && !blob.type.startsWith("image/")) return false
    return cacheImageBlob(fileId, blob, url)
  })()
  inFlight.set(key, work)
  try { return await work } finally { inFlight.delete(key) }
}

export async function warmImageCache(files = []) {
  const images = files.filter((file) => file.fileUrl && (!file.mimeType || file.mimeType.startsWith("image/")) &&
    (!file.fileType || file.fileType === "image" || file.fileType.startsWith("image/")))
  let index = 0
  const worker = async () => {
    while (index < images.length && navigator.onLine !== false) {
      const file = images[index++]
      await cacheImage(file._id || file.id || file._clientId, file.fileUrl)
    }
  }
  await Promise.all(Array.from({ length: Math.min(3, images.length) }, worker))
}
export const isImageCached = async (fileId) => Boolean(await getCachedImageBlob(fileId))
export async function evictCachedImage(fileId, url) {
  // Clear blob values using the existing IndexedDB cache API.
  await Promise.all(keys(fileId, url).map((key) => putCachedData(key, null).catch(() => {})))
  if (fileId && typeof caches !== "undefined") {
    try { await (await caches.open(LEGACY_CACHE)).delete(new Request(LEGACY_ORIGIN + encodeURIComponent(fileId))) } catch {}
  }
}
